import { forkJoin } from 'rxjs/observable/forkJoin';
import { orderBy } from 'lodash';
import { SetAppointments } from '../../../ng2/main/appointments/appointments.actions';
import { authenticatedPatientSelector, currentPatientSelector } from '../../selectors/patients-selectors.ts';

module.exports = {
  templateUrl: 'main/components/availabilities/availabilities.html',
  controller: class AvailabilitiesController {

    static $inject = [
      '$filter',
      '$interval',
      '$ngRedux',
      '$scope',
      '$state',
      'Appointments',
      'AppointmentsService',
      'Availabilities',
      'LoadingService',
      'ProductService',
      'ReasonsService',
      '$translate',
    ];

    // We give us a 30 seconds buffer to avoid issues due to network latency / client side processing time, etc...
    defaultTimeBuffer = 30;

    constructor(
      $filter,
      $interval,
      $ngRedux,
      $scope,
      $state,
      Appointments,
      AppointmentsService,
      Availabilities,
      LoadingService,
      ProductService,
      ReasonsService,
      $translate,
    ) {
      this.$filter = $filter;
      this.$interval = $interval;
      this.$ngRedux = $ngRedux;
      this.$scope = $scope;
      this.$state = $state;
      this.Appointments = Appointments;
      this.AppointmentsService = AppointmentsService;
      this.Availabilities = Availabilities;
      this.LoadingService = LoadingService;
      this.ProductService = ProductService;
      this.ReasonsService = ReasonsService;
      this.$translate = $translate;
    }

    $onInit() {
      this.setStateConnection();

      if (this.ProductService.isAvailable('PATIENT_FORMS') && this.reason && this.reason.form && this.reason.form.questionsCount) {
        const answers = this.$ngRedux.getState().reasonForm.answers;
        // If reason has a form and there is no answer registered, go back to form page
        if (!!answers && !Object.keys(answers).length) {
          this.$state.go('patients.id.reasons.id.form');
        }
      }

      this.availabilities = [];

      this.fallbackAvailabilities = [];
      this.selectedAvailabilities = [];

      this.noMoreAvailabilities = false;
      this.noAvailabilities = false;
      this.initialLoadDone = false;
      this.timeElapsed = false;
      this.hasConfirmationFailed = false;
      this.takingAppointment = false;

      this.confirmChangeState = false;
      this.showNavigationDialog = false;
      this.toState = null;
      this.toParams = null;
      this.noFallbackAvailabilities = true;
      this.appointmentType = null;

      this.isBlocked = this.authenticatedPatient.subscription.preventBooking;
      this.staticAvailabilitiesMessage = this.getStaticAvailabilitiesMessage();

      if (this.notifications[0]) {
        this.lastPeriodEndDate = this.notifications[0].lastPeriodEndDate;
      }

      if (this.currentPatient.subscription.account) {
        this.familyDoctor = this.currentPatient.subscription.account;
      }

      this.eligibleAccountsIds = [];

      this.fetchAllAvailabilities();

      this.$scope.$on('onNavigationStart', (event, data) => {
        if (this.selectedAvailabilities.length) {
          event.preventDefault();
          this.afterConfirmCallback = data.callback;
          this.showNavigationDialog = true;
        }
      });

      this.$scope.$on('$stateChangeStart', (event, toState, toParams) => {
        if (toState.name === 'patients.id.appointments.id' || toState.name === 'patients.id.appointments.confirmation') {
          return;
        }

        if (this.$state.current.name == toState.name) {
          this.unlockAllAvailabilities();

          return;
        }

        if (!this.confirmChangeState && this.haveAvailabilities()) {
          event.preventDefault();

          this.toState = toState;
          this.toParams = toParams;

          this.showNavigationDialog = true;
        }
      });
    }

    getStaticAvailabilitiesMessage() {
      if (this.$translate.use() === 'fr_CA') {
        return this.$filter('linky')(this.reason.resultPageMessageFrCa, '_blank');
      }

      return this.$filter('linky')(this.reason.resultPageMessageEn, '_blank');
    }

    setStateConnection() {
      this.$scope.$on('$destroy', this.$ngRedux.connect(state => ({
        authenticatedPatient: authenticatedPatientSelector(state),
        appointments: state.appointments,
        currentPatient: currentPatientSelector(state),
        group: state.group,
        notifications: state.notifications,
        reason: state.reasons.current,
        reasonForm: state.reasonForm,
        offering: state.offering,
        selectedPatients: state.reasons.selectedPatients,
      }))(this));
    }

    onConfirmRouteChange() {
      this.showNavigationDialog = false;
      this.confirmChangeState = true;

      if (!this.toState || this.toState.name !== 'patients.id.appointments.id') {
        this.destroyTimer();

        this.unlockAllAvailabilities();
      }

      if (this.toState) {
        this.$state.go(this.toState.name, this.toParams);
      } else if (this.afterConfirmCallback) {
        this.afterConfirmCallback();
      }
    }

    onCancelRouteChange() {
      this.showNavigationDialog = false;
      this.afterConfirmCallback = null;
      this.toState = null;
      this.toParams = null;
    }

    unlockAvailabilities(availabilities) {
      availabilities.forEach(availability => availability.$unlock(+this.$state.params.patientId));
    }

    unlockAllAvailabilities() {
      if (this.availabilities && this.availabilities.length) {
        this.unlockAvailabilities(this.availabilities);
      }

      if (this.fallbackAvailabilities && this.fallbackAvailabilities.length) {
        this.unlockAvailabilities(this.fallbackAvailabilities);
      }
    }

    changeSelectedAvailabilities(availabilities) {
      this.selectedAvailabilities = availabilities;
    }

    selectAvailabilities(selectedAvailability) {
      if (!selectedAvailability) {
        this.selectedAvailabilities = this.availabilities.length ? this.availabilities : this.fallbackAvailabilities;
      } else {
        this.selectedAvailabilities = [selectedAvailability];
      }

      this.LoadingService.show({
        token: 'loading.appointment',
        longOperation: true,
        autoHide: false,
      });

      if (this.isDoubleBooking) {
        return this.selectAvailabilitiesDoubleBooking(this.selectedAvailabilities);
      }

      forkJoin(
        this.selectedAvailabilities.map((availability, index) => this.AppointmentsService.create(this.setAppointmentsParameter(availability, index))),
      )
      .subscribe(
        (appointments) => {
          this.unlockAvailabilities(this.selectedAvailabilities);
          this.$ngRedux.dispatch(new SetAppointments(appointments));
          this.selectedAvailabilities = [];

          if (appointments.length > 1) {
            this.$state.go('patients.id.appointments.confirmation');
          } else {
            this.$state.go('patients.id.appointments.id', { appointmentId: appointments[0].id });
          }
        },
        undefined,
        () => {
          if (!this.selectedAvailabilities.length) {
            return;
          }

          this.hasConfirmationFailed = true;
          this.selectedAvailabilities = [];
          this.takingAppointment = false;
          // We must manually hide the loading layer
          this.LoadingService.hide();
        },
      );
    }

    selectAvailabilitiesDoubleBooking(availabilities) {
      const params = this.setMultipleAppointmentsParameter(availabilities);

      this.AppointmentsService.createMultiple(params)
        .subscribe(
          (appointments) => {
            this.unlockAvailabilities(this.selectedAvailabilities);
            this.$ngRedux.dispatch(new SetAppointments(appointments));
            this.selectedAvailabilities = [];

            this.$state.go('patients.id.appointments.confirmation');
          },
          undefined,
          () => {
            if (!this.selectedAvailabilities.length) {
              return;
            }

            this.hasConfirmationFailed = true;
            this.selectedAvailabilities = [];
            this.takingAppointment = false;
            // We must manually hide the loading layer
            this.LoadingService.hide();
          },
        );
    }

    setMultipleAppointmentsParameter(availabilities) {
      const patientId = this.currentPatient.id;

      const requestData = {
        availabilityIds: availabilities.map(({ id }) => id),
        patientId,
        visitReason: this.reason.selectedVisitReason,
        requestSessionUuid: this.offering.requestSessionUuid,
        groupId: this.group.id,
      };

      if (!!this.reasonForm.answers && Object.keys(this.reasonForm.answers).length) {
        requestData.formAnswer = {
          formId: this.reasonForm.form.id,
          answers: this.reasonForm.answers[patientId],
        };
      }
      return requestData;
    }

    setAppointmentsParameter(availability, index) {
      const patientId = this.selectedPatients.length ? this.selectedPatients[index].id : this.currentPatient.id;

      const requestData = {
        availabilityId: availability.id,
        patientId,
        visitReason: this.reason.selectedVisitReason,
        requestSessionUuid: this.offering.requestSessionUuid,
      };

      switch (availability.task.canBeVirtual) {
        case 0:
          this.appointmentType = 'inPerson';
          break;
        case 2:
          this.appointmentType = 'virtual';
          break;
        default:
      }

      if (this.appointmentType) {
        requestData.appointmentType = this.appointmentType;
      }

      if (!!this.reasonForm.answers && Object.keys(this.reasonForm.answers).length) {
        requestData.formAnswer = {
          formId: this.reasonForm.form.id,
          answers: this.reasonForm.answers[patientId],
        };
      }
      return requestData;
    }

    augmentWithTimePreference(options) {
      if (this.reason.allowTimePreference) {
        const opts = {};

        if (this.reason.preferredDate) {
          opts.preferred_date = this.reason.preferredDate;
        }

        if (this.reason.startDate) {
          opts.start_date = this.reason.startDate;
        }

        if (this.reason.preferences) {
          opts['preferred_moments[]'] = this.reason.preferences;
        }

        return Object.assign({}, options, opts);
      }
      return options;
    }

    fetchFallbackAvailabilities() {
      const fallbackReason = this.reason.fallbackReason;

      let opts = {
        group_id: this.group.id,
        offering_id: fallbackReason.offering.id,
        reason_id: fallbackReason.id,
        patient_id: this.$state.params.patientId,
        request_session_uuid: this.offering.requestSessionUuid,
        request_kind: 'fallback',
      };
      // If there is no subscription we must not send a request with nil values.
      if (this.currentPatient.subscription.account) {
        // The fallback reason may not have the same account filter as the main reason
        // Adjust the options accordingly
        if (fallbackReason.accountFilter === 'required') {
          opts.account_id = this.currentPatient.subscription.account.id;
        }

        // Pass on the time preferences to the fallback reason request - if allowed by its configuration
        if (fallbackReason.allowTimePreference) {
          opts = this.augmentWithTimePreference(opts);
        }

        const request = this.Availabilities.query(opts, (availabilities) => {
          this.fallbackAvailabilities = availabilities;

          this.noFallbackAvailabilities = availabilities.length === 0;

          if (!this.noFallbackAvailabilities) {
            this.resetTimer(availabilities);
          }

          this.timeElapsed = false;
        }, () => {
          this.noFallbackAvailabilities = true;

          this.timeElapsed = false;
        }).$promise;

        this.LoadingService.waitFor(request);
      } else {
        this.noFallbackAvailabilities = true;
      }
    }

    findTaskId(form) {
      const answer = _.findLast(form, aswr => !!aswr.taskId);
      if (answer) {
        return answer.taskId;
      }
      return null;
    }

    fetchAvailabilities(historic = false) {
      if (this.isFetchAvailabilitiesEnabled()) {
        const opts = {
          group_id: this.group.id,
          offering_id: this.$state.params.offeringId,
          reason_id: this.$state.params.reasonId,
          patient_id: this.$state.params.patientId,
          task_id: this.findTaskId(this.reasonForm.answers[this.currentPatient.id]),
          request_session_uuid: this.offering.requestSessionUuid,
          request_kind: 'normal',
        };

        if (this.reason.accountFilter === 'required' && this.currentPatient.subscription.account) {
          opts.account_id = this.currentPatient.subscription.account.id;
        } else if (this.reason.accountFilter === 'optional' && (this.reason.allowSelectHcp || this.ProductService.isAvailable('PATIENT_AUTHORIZED_PROFESSIONALS'))) {
          opts['account_ids[]'] = this.reason.eligibleAccountsIds;
        }

        if (historic) {
          opts.filter_out_shown = true;
        }

        if (this.selectedPatients.length) {
          opts['consecutive_patient_ids[]'] = this.selectedPatients.map(patient => patient.id);
          opts['consecutive_task_ids[]'] = this.selectedPatients.map((patient) => {
            const taskId = this.findTaskId(this.reasonForm.answers[patient.id]);
            return taskId || -1;
          });
        }

        if (this.reason.consecutiveDelay) {
          opts.consecutive_delay = this.reason.consecutiveDelay;
        }

        if (!!this.selectedAvailabilities.length) {
          const startTime = this.selectedAvailabilities[0].startTime;
          opts['last_shown_start_time'] = startTime;
        }

        this.isLoading = true;
        this.appointmentType = null;

        const request = this.Availabilities.query(this.augmentWithTimePreference(opts), (availabilities) => {
          if (this.availabilities.length && availabilities.length) {
            this.noMoreAvailabilities = availabilities[0].id === this.availabilities[0].id;
          }

          if (availabilities.length) {
            this.resetTimer(availabilities);
            this.noAvailabilities = false;
            this.availabilities = availabilities;

            this.changeSelectedAvailabilities(this.availabilities);
          } else {
            this.noAvailabilities = true;
          }
          this.initialLoadDone = true;
          this.timeElapsed = false;
          this.isLoading = false;
        }, () => {
          this.noAvailabilities = true;
          this.noMoreAvailabilities = true;

          this.initialLoadDone = true;
          this.timeElapsed = false;
          this.isLoading = false;
        }).$promise;

        if (!historic) {
          this.LoadingService.waitFor(request, { token: 'loading.availability', longOperation: true });
        }
      }
    }

    fetchAllAvailabilities() {
      this.hasConfirmationFailed = false;

      if (this.reason.fallbackReason && this.selectedPatients.length <= 1) {
        this.fetchFallbackAvailabilities();
      }

      this.fetchAvailabilities();
    }

    isFetchAvailabilitiesEnabled() {
      return !this.noMoreAvailabilities;
    }

    shouldDisplayFallbackAvailabilities() {
      if (!this.isLoading && !this.noFallbackAvailabilities) {
        if (!this.selectedAvailabilities.length || (this.selectedAvailabilities.length && !this.selectedAvailabilities.filter(availability => availability.task.reason.fallbackReasonOnlyWhenNone).length)) {
          return true;
        }
      }
      return false;
    }

    findNoAvailabilityMessage() {
      const answer = _.findLast(this.reasonForm.answers[this.currentPatient.id], aswr => !!aswr.noAvailabilityMessage);
      if (answer) {
        return answer.noAvailabilityMessage;
      }
      return null;
    }

    haveAvailabilities() {
      return this.initialLoadDone && !this.timeElapsed && (!this.noAvailabilities || !this.noFallbackAvailabilities);
    }

    showDateWarning() {
      return !!this.lastPeriodEndDate && !!this.reason.preferredDate && moment(this.reason.preferredDate) > moment(this.lastPeriodEndDate);
    }

    showWarning() {
      return !this.haveAvailabilities() && !this.timeElapsed && !this.LoadingService.isLoading;
    }

    noAvailabilityErrorMessage() {
      if (this.findNoAvailabilityMessage()) {
        return 'dateOfLastAppointmentAnswered';
      } else if (this.showDateWarning()) {
        return 'hasDatePreference';
      } else if (!this.ReasonsService.hasPreferences(this.reason)) {
        return 'hasNoPreferences';
      } else if (this.ReasonsService.hasPreferences(this.reason)) {
        return 'hasPreferences';
      }
      return null;
    }

    hasError() {
      return this.initialLoadDone && (this.timeElapsed || (this.noAvailabilities && this.noFallbackAvailabilities) || this.hasConfirmationFailed);
    }

    resetTimer(availabilities) {
      this.destroyTimer();

      if (availabilities.length) {
        this.initializeTimer(availabilities[0].lockRemainingSeconds - this.defaultTimeBuffer);
      }
    }

    onTimerUpdated() {
      this.updateRemainingTime();

      if (!this.takingAppointment && this.remainingTime <= 0) {
        this.availabilities = [];
        this.fallbackAvailabilities = [];
        this.noMoreAvailabilities = false;
        this.timeElapsed = true;
        this.selectedAvailabilities = [];
      }
    }

    initializeTimer(lockRemainingSeconds) {
      this.timeElapsed = false;
      this.expirationTimestamp = moment().unix() + lockRemainingSeconds;

      this.updateRemainingTime();

      // Skip timer interval in test mode
      if (application.env === 'test') {
        return;
      }

      this.updateTimerId = this.$interval(this.onTimerUpdated.bind(this), 1000);
    }

    updateRemainingTime() {
      this.remainingTime = Math.max(this.expirationTimestamp - moment().unix(), 0);
    }

    destroyTimer() {
      this.$interval.cancel(this.updateTimerId);
    }

    reasonPatientMessage() {
      if (this.currentPatient.subscription.account) {
        return this.reason.patientMessageWithFamilyDoctor;
      }
      return this.reason.patientMessageWithoutFamilyDoctor;
    }

    get isWalkin() {
      return this.selectAvailability && this.selectAvailability.task && this.selectAvailability.task.reason.offering.code === 'WALK_IN';
    }

    get availabilitiesWithPatientByTime() {
      return [...orderBy(this.availabilities, [availability => moment(availability.startTime)])].map((orderedAvailability) => {
        const index = this.availabilities.findIndex(availability => availability === orderedAvailability);
        return Object.assign(orderedAvailability, {
          selectedPatient: this.selectedPatients[index],
        });
      });
    }

    get isDoubleBooking() {
      return this.ProductService.isAvailable('PATIENT_MULTI_BOOKING');
    }
  },
};



// WEBPACK FOOTER //
// ./main/components/availabilities/availabilities.js